"use client"

import { Link } from "react-router-dom"
import { AlertTriangleIcon, CheckCircle2Icon, ChevronRightIcon } from "lucide-react"

import { HomeTile } from "@/features/dashboard/home-d4/components/home-tile"
import {
  monthShortLabel,
  type MarginRow,
  type MonthNetBar,
  type StrugglingSummary,
} from "@/features/finance/overview/church-f3/lib/church-f3-format"
import {
  EXPENSE_BAR_CLASS,
  FALLBACK_MUTED_CLASS,
  INCOME_BAR_CLASS,
  TrackBar,
  type TileFallback,
} from "@/features/finance/overview/church-f3/components/church-tile-parts"
import { useLocale, useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"

/** A streak is worth saying from the second losing month on: one bad month is a month, two is a trend. */
const STREAK_WORTH_SAYING = 2

/**
 * Struggling (F3): branches whose expenses exceeded their income this period. The worst one gets its last three
 * months of net as small bars and, from the second losing month on, how long it has lasted. When no branch is in
 * deficit the tile says so: that is a result, not an absence.
 */
export function ChurchStrugglingTile({
  summary,
  currency,
  detailHref,
  closest = [],
  fallback = null,
}: {
  summary: StrugglingSummary
  currency: string
  /** The worst branch's Branch Details finance page, or null (the parish has no Branch Details). */
  detailHref: string | null
  /** When no branch is in deficit: the thinnest margins, to watch. */
  closest?: readonly MarginRow[]
  /** Set when the tile shows the previous period because this one has no money yet. */
  fallback?: TileFallback | null
}) {
  const t = useTranslations("finance.overview")
  const { worst, detail, deficits } = summary

  return (
    <HomeTile
      title={t("widgets.churchStruggling.title")}
      subtitle={fallback?.label ?? t("widgets.churchStruggling.subtitle")}
      testId='church-tile-struggling'
    >
      {!worst ? (
        <>
          <div
            data-testid='church-struggling-none'
            className='flex items-start gap-2.5 rounded-xl bg-teal-50 px-3 py-2.5 text-[13px] dark:bg-teal-950/40'
          >
            <CheckCircle2Icon aria-hidden className='mt-0.5 size-4 shrink-0 text-teal-700 dark:text-teal-300' />
            <span className='flex flex-col gap-0.5'>
              <b className='text-foreground'>{t("church.struggling.none.title")}</b>
              <span className='text-muted-foreground'>{t("church.struggling.none.description")}</span>
            </span>
          </div>
          {closest.length > 0 ? (
            <div data-testid='church-struggling-closest' className='flex flex-col gap-2.5'>
              <span className='text-[12.5px] text-muted-foreground'>{t("church.struggling.closest")}</span>
              {closest.map((row) => (
                <div key={row.id} className='flex flex-col gap-1'>
                  <span className='flex gap-2 text-[12.5px]'>
                    <span className='truncate font-semibold text-foreground'>{row.name}</span>
                    <span className='ml-auto font-bold whitespace-nowrap tabular-nums text-amber-700 dark:text-amber-300'>
                      {t("church.struggling.margin", { value: Math.round(row.marginPercent) })}
                    </span>
                  </span>
                  <span className={fallback ? FALLBACK_MUTED_CLASS : undefined}>
                    <TrackBar ratio={Math.min(1, Math.max(0, row.marginPercent) / 100)} className='bg-amber-500 dark:bg-amber-400' />
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className='flex items-start gap-2.5 rounded-xl bg-rose-50 px-3 py-2.5 text-[13px] dark:bg-rose-950/40'>
            <AlertTriangleIcon aria-hidden className='mt-0.5 size-4 shrink-0 text-rose-600 dark:text-rose-400' />
            <span data-testid='church-struggling-headline' className='text-foreground'>
              <b>{worst.name}</b>
              {t("church.struggling.headline", { amount: formatCurrency(worst.net, currency) })}
              {detail && detail.streak >= STREAK_WORTH_SAYING ? t("church.struggling.streak", { count: detail.streak }) : null}
            </span>
          </div>
          {detail && detail.months.length > 0 ? (
            <>
              <span className='text-xs font-bold text-muted-foreground'>
                {t("church.struggling.recentLabel", { name: worst.name, count: detail.months.length })}
              </span>
              <MonthNetBars months={detail.months} currency={currency} />
            </>
          ) : null}
          <div className='mt-auto flex flex-col gap-1'>
            {deficits.length > 1 ? (
              <span className='text-xs text-muted-foreground'>{t(deficits.length === 2 ? "church.struggling.othersOne" : "church.struggling.othersOther", {
                  count: deficits.length - 1,
                })}</span>
            ) : null}
            {detailHref ? (
              <Link
                to={detailHref}
                className='inline-flex items-center gap-0.5 self-start rounded-md text-[13px] font-bold text-primary hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
              >
                {t("church.struggling.link", { name: worst.name })}
                <ChevronRightIcon aria-hidden className='size-3.5' />
              </Link>
            ) : null}
          </div>
        </>
      )}
    </HomeTile>
  )
}

/**
 * The months as bars hanging from one baseline: a loss hangs below it in rose, a surplus stands above it in
 * teal, both on the scale of the largest month. The latest month is drawn solid, the earlier ones lighter.
 */
function MonthNetBars({ months, currency }: { months: MonthNetBar[]; currency: string }) {
  const locale = useLocale()
  const anyPositive = months.some((month) => !month.isNegative && month.ratio > 0)
  const anyNegative = months.some((month) => month.isNegative)

  return (
    <div data-testid='church-struggling-months' className='flex gap-2.5'>
      {months.map((month, index) => {
        const latest = index === months.length - 1
        return (
          <span key={month.month} className='flex flex-1 flex-col items-center gap-0.5'>
            {anyPositive ? (
              <span className='flex h-6 w-full items-end'>
                {!month.isNegative ? (
                  <span
                    className={cn("w-full rounded-t-[4px]", INCOME_BAR_CLASS, latest ? "" : "opacity-50")}
                    style={{ height: `${month.ratio * 100}%` }}
                  />
                ) : null}
              </span>
            ) : null}
            <span aria-hidden className='h-px w-full bg-border' />
            {anyNegative ? (
              <span className='flex h-9 w-full items-start'>
                {month.isNegative ? (
                  <span
                    className={cn("w-full rounded-b-[4px]", EXPENSE_BAR_CLASS, latest ? "" : "opacity-50")}
                    style={{ height: `${Math.max(4, month.ratio * 100)}%` }}
                  />
                ) : null}
              </span>
            ) : null}
            <span className='text-[11px] font-bold whitespace-nowrap tabular-nums text-foreground'>
              {formatCurrency(month.net, currency)}
            </span>
            <span className='text-[10.5px] text-muted-foreground'>{monthShortLabel(month.month, locale)}</span>
          </span>
        )
      })}
    </div>
  )
}
